import { useEffect, useRef, useState } from 'react';
import { StateBadge } from '@wyocspay/ui';
import { badgeLabel } from './format.js';
import { ApiError, api } from './api.js';

/**
 * Inline payment-status editor.
 *
 * Reads as a normal status badge; double-click it and it becomes a dropdown of
 * the states this payment may move to (the server decides which - only valid
 * transitions). Pick one and it saves immediately and the row updates, no page
 * reload. Escape or clicking away cancels. The server audits every change and
 * moves no money - this only corrects the local record (e.g. a stuck charge).
 */
export function StatusEditor({
  paymentId,
  state,
  onChanged,
}: {
  paymentId: string;
  state: string;
  onChanged?: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [options, setOptions] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const boxRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!editing) return;
    setOptions(null);
    setErr(null);
    let live = true;
    api
      .paymentStatusOptions(paymentId)
      .then((r) => {
        if (live) setOptions(r.options);
      })
      .catch((e) => {
        if (live) setErr(e instanceof ApiError ? e.message : String(e));
      });
    return () => {
      live = false;
    };
  }, [editing, paymentId]);

  // Cancel on click outside or Escape.
  useEffect(() => {
    if (!editing) return;
    function onDown(e: MouseEvent): void {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setEditing(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setEditing(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [editing]);

  async function choose(next: string): Promise<void> {
    if (next === state) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.setPaymentStatus(paymentId, next);
      setEditing(false);
      onChanged?.(next);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <span
        ref={boxRef}
        onDoubleClick={() => setEditing(true)}
        title="Двоен клик за смяна на статуса"
        style={{ cursor: 'pointer', display: 'inline-flex' }}
      >
        <StateBadge state={state} label={badgeLabel(state)} />
      </span>
    );
  }

  return (
    <span ref={boxRef} style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
      <select
        className="wc-input"
        style={{ width: 200, fontSize: 13 }}
        autoFocus
        disabled={busy || !options}
        value={state}
        onChange={(e) => void choose(e.target.value)}
      >
        {!options ? (
          <option>Зареждане…</option>
        ) : (
          options.map((s) => (
            <option key={s} value={s}>
              {badgeLabel(s)}
              {s === state ? ' (сега)' : ''}
            </option>
          ))
        )}
      </select>
      {err ? <span style={{ fontSize: 11.5, color: 'var(--wc-danger)' }}>{err}</span> : null}
    </span>
  );
}
